import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  AvailablePreTestConfigurationOptions,
  AvailableProgressPageTestConfigurationOptions,
  TestConfiguration,
} from "types/testTypes";
import { TestSuiteOverview } from "@utils/OverviewUtils";

export type TestSuiteItemStatus =
  | "not started"
  | "loading"
  | "completed"
  | "error"
  | "skipped"
  | "terminated";
export interface TestSuiteItem {
  id: string;
  configuration: TestConfiguration;
  availableConfigurations: AvailablePreTestConfigurationOptions | null;
  availableProgressConfigurations: AvailableProgressPageTestConfigurationOptions | null;
  progressConfiguration: TestConfiguration | null;
  status: TestSuiteItemStatus;
  progress?: number;
  completedTestResultId?: string;
}

export interface TestSuiteState {
  tests: TestSuiteItem[];
  activeTestIndex: number | null;
  selectedTestIndex: number | null;
  suiteId: string | null;
  suiteName: string | null;
  sessionId: string | null;
  sessionResultReplacement: Record<string, string>;
  completedTestIds: string[];
  autoPlay: boolean;
  autoPlayCountdown: number | null;
  skipNextAutoPlay: boolean;
  suiteRunStartedAt: number | null;
  suiteRunEndedAt: number | null;
  sharedTestConfiguration: Partial<TestConfiguration> | null;
  pendingPlayAfterSave: boolean;
  firstTestCompleted: boolean;
  calibrationEnabledTestId: string | null;
  overview: TestSuiteOverview | null;
}

export const initialTestSuiteState: TestSuiteState = {
  tests: [],
  activeTestIndex: null,
  selectedTestIndex: null,
  suiteId: null,
  suiteName: null,
  sessionId: null,
  sessionResultReplacement: {},
  completedTestIds: [],
  autoPlay: false,
  autoPlayCountdown: null,
  skipNextAutoPlay: false,
  suiteRunStartedAt: null,
  suiteRunEndedAt: null,
  sharedTestConfiguration: null,
  pendingPlayAfterSave: false,
  firstTestCompleted: false,
  calibrationEnabledTestId: null,
  overview: null,
};

const initialState = initialTestSuiteState;

const TERMINAL_TEST_STATUSES: ReadonlySet<TestSuiteItemStatus> = new Set([
  "completed",
  "terminated",
  "skipped",
  "error",
]);

function withDefaultCalibrationSceneSetting(
  configuration: TestConfiguration,
  isFirstTestInSuite: boolean,
): TestConfiguration {
  if (configuration.startWithCalibrationScene !== undefined) {
    return configuration;
  }

  return {
    ...configuration,
    startWithCalibrationScene: isFirstTestInSuite,
  };
}

function cloneSuiteItemWithDefaultCalibrationSetting(
  test: TestSuiteItem,
  index: number,
): TestSuiteItem {
  return {
    ...test,
    configuration: withDefaultCalibrationSceneSetting(test.configuration, index === 0),
  };
}

export function buildRestartConfiguration(
  configuration: TestConfiguration,
  firstTestCompleted: boolean,
  calibrationEnabled = false,
): TestConfiguration {
  if (!firstTestCompleted || calibrationEnabled) return configuration;
  return { ...configuration, startWithCalibrationScene: false };
}

function parseCompletedTestResultIds(completedTestResultId: string | undefined): string[] {
  if (!completedTestResultId) return [];
  return completedTestResultId
    .split(",")
    .map((id) => id.trim())
    .filter(Boolean);
}

function disableCalibrationSceneForSuiteTests(
  tests: TestSuiteItem[],
  calibrationEnabledTestId: string | null = null,
): TestSuiteItem[] {
  return tests.map((test) => ({
    ...test,
    configuration: {
      ...test.configuration,
      startWithCalibrationScene:
        test.id === calibrationEnabledTestId ? test.configuration.startWithCalibrationScene : false,
    },
  }));
}

function retargetCalibrationToNextRunnable(state: TestSuiteState, fromIndex: number): void {
  const previous = state.tests.find((test) => test.id === state.calibrationEnabledTestId);
  if (previous) {
    previous.configuration.startWithCalibrationScene = false;
  }
  const nextRunnable = state.tests.find(
    (test, index) => index > fromIndex && !TERMINAL_TEST_STATUSES.has(test.status),
  );
  if (nextRunnable) {
    nextRunnable.configuration.startWithCalibrationScene = true;
    state.calibrationEnabledTestId = nextRunnable.id;
  } else {
    state.calibrationEnabledTestId = null;
  }
}

function reassignCalibrationSceneSettingAfterReorder(
  tests: TestSuiteItem[],
  shouldStartWithCalibrationOnFirstTest: boolean,
): TestSuiteItem[] {
  if (!shouldStartWithCalibrationOnFirstTest) {
    return tests;
  }

  return tests.map((test, index) => ({
    ...test,
    configuration: {
      ...test.configuration,
      startWithCalibrationScene: index === 0,
    },
  }));
}

const testSuiteSlice = createSlice({
  name: "testSuite",
  initialState,
  reducers: {
    addTest: (
      state,
      action: PayloadAction<{
        id?: string;
        configuration: TestConfiguration;
        availableConfigurations?: AvailablePreTestConfigurationOptions | null;
        availableProgressConfigurations?: AvailableProgressPageTestConfigurationOptions | null;
        progressConfiguration?: TestConfiguration | null;
        status: TestSuiteItemStatus;
        progress: number;
      }>,
    ) => {
      const isFirstTestInSuite = state.tests.length === 0;
      const newTest: TestSuiteItem = {
        id: action.payload.id,
        configuration: withDefaultCalibrationSceneSetting(
          action.payload.configuration,
          isFirstTestInSuite,
        ),
        availableConfigurations: action.payload.availableConfigurations ?? null,
        availableProgressConfigurations: action.payload.availableProgressConfigurations ?? null,
        progressConfiguration: action.payload.progressConfiguration ?? null,
        status: action.payload.status,
        progress: action.payload.progress,
      };
      state.tests.push(newTest);
      state.selectedTestIndex = state.tests.length - 1;
    },
    removeTest: (state, action: PayloadAction<string>) => {
      state.tests = state.tests.filter((test) => test.id !== action.payload);
      if (state.activeTestIndex !== null && state.tests.length <= state.activeTestIndex) {
        state.activeTestIndex = null;
      }
      if (state.selectedTestIndex !== null && state.tests.length <= state.selectedTestIndex) {
        state.selectedTestIndex = null;
      }
    },
    updateTest: (state, action: PayloadAction<{ id: string; updates: Partial<TestSuiteItem> }>) => {
      const index = state.tests.findIndex((test) => test.id === action.payload.id);
      if (index !== -1) {
        state.tests[index] = {
          ...state.tests[index],
          ...action.payload.updates,
        };
      }
    },
    updateTestConfiguration: (
      state,
      action: PayloadAction<{ id: string; testSuiteItem: TestSuiteItem }>,
    ) => {
      const index = state.tests.findIndex((test) => test.id === action.payload.id);
      if (index !== -1) {
        state.tests[index] = action.payload.testSuiteItem;
        if (
          state.calibrationEnabledTestId === action.payload.id &&
          !action.payload.testSuiteItem.configuration.startWithCalibrationScene
        ) {
          state.calibrationEnabledTestId = null;
        }
      }
    },
    updateTestStatus: (
      state,
      action: PayloadAction<{ id: string; status: TestSuiteItemStatus; progress?: number }>,
    ) => {
      const index = state.tests.findIndex((test) => test.id === action.payload.id);
      if (index !== -1) {
        const { status } = action.payload;
        state.tests[index].status = status;
        if (action.payload.progress !== undefined) {
          state.tests[index].progress = action.payload.progress;
        }
        const calibrationTargetLostBeforeCompletion =
          state.tests[index].id === state.calibrationEnabledTestId &&
          (status === "terminated" || status === "skipped" || status === "error");
        if (calibrationTargetLostBeforeCompletion) {
          retargetCalibrationToNextRunnable(state, index);
        }
      }
    },
    reorderTests: (state, action: PayloadAction<{ fromIndex: number; toIndex: number }>) => {
      const { fromIndex, toIndex } = action.payload;
      const shouldReassignCalibrationScene =
        state.tests[0]?.configuration.startWithCalibrationScene === true;
      const [moved] = state.tests.splice(fromIndex, 1);
      state.tests.splice(toIndex, 0, moved);
      state.tests = reassignCalibrationSceneSettingAfterReorder(
        state.tests,
        shouldReassignCalibrationScene,
      );

      if (state.activeTestIndex !== null) {
        let newActiveIndex = state.activeTestIndex;
        if (state.activeTestIndex === fromIndex) {
          newActiveIndex = toIndex;
        } else if (state.activeTestIndex > fromIndex && state.activeTestIndex <= toIndex) {
          newActiveIndex = state.activeTestIndex - 1;
        } else if (state.activeTestIndex < fromIndex && state.activeTestIndex >= toIndex) {
          newActiveIndex = state.activeTestIndex + 1;
        }
        state.activeTestIndex = newActiveIndex;
      }

      if (state.selectedTestIndex !== null) {
        let newSelectedIndex = state.selectedTestIndex;
        if (state.selectedTestIndex === fromIndex) {
          newSelectedIndex = toIndex;
        } else if (state.selectedTestIndex > fromIndex && state.selectedTestIndex <= toIndex) {
          newSelectedIndex = state.selectedTestIndex - 1;
        } else if (state.selectedTestIndex < fromIndex && state.selectedTestIndex >= toIndex) {
          newSelectedIndex = state.selectedTestIndex + 1;
        }
        state.selectedTestIndex = newSelectedIndex;
      }
    },
    setActiveTest: (state, action: PayloadAction<number | null>) => {
      state.activeTestIndex = action.payload;
    },
    setSelectedTest: (state, action: PayloadAction<number | null>) => {
      state.selectedTestIndex = action.payload;
    },
    moveToNextTest: (state) => {
      const currentIndex = state.activeTestIndex ?? -1;

      const nextIndex = state.tests.findIndex(
        (test, index) => index > currentIndex && !TERMINAL_TEST_STATUSES.has(test.status),
      );

      state.activeTestIndex = nextIndex === -1 ? null : nextIndex;
    },
    clearTestSuite: (state) => {
      state.tests = [];
      state.activeTestIndex = null;
      state.selectedTestIndex = null;
      state.suiteId = null;
      state.suiteName = null;
      state.sessionId = null;
      state.sessionResultReplacement = {};
      state.completedTestIds = [];
      state.autoPlay = false;
      state.autoPlayCountdown = null;
      state.skipNextAutoPlay = false;
      state.suiteRunStartedAt = null;
      state.suiteRunEndedAt = null;
      state.sharedTestConfiguration = null;
      state.pendingPlayAfterSave = false;
      state.firstTestCompleted = false;
      state.calibrationEnabledTestId = null;
      state.overview = null;
    },
    addCompletedTestIdToSuite: (
      state,
      action: PayloadAction<{ testSuiteTestId: string; completedTestResultId: string }>,
    ) => {
      const { testSuiteTestId, completedTestResultId } = action.payload;
      const newIds = parseCompletedTestResultIds(completedTestResultId);
      const newIdSet = new Set(newIds);
      const testIndex = state.tests.findIndex((test) => test.id === testSuiteTestId);
      if (testIndex !== -1) {
        const previousIds = parseCompletedTestResultIds(
          state.tests[testIndex].completedTestResultId,
        );
        const orphanedIds = new Set(previousIds.filter((id) => !newIdSet.has(id)));
        if (orphanedIds.size > 0) {
          state.completedTestIds = state.completedTestIds.filter((id) => !orphanedIds.has(id));
        }
        state.tests[testIndex].completedTestResultId = completedTestResultId;
        if (testIndex === 0) {
          state.firstTestCompleted = true;
          state.tests = disableCalibrationSceneForSuiteTests(
            state.tests,
            state.calibrationEnabledTestId,
          );
        }
        if (state.calibrationEnabledTestId === testSuiteTestId) {
          state.calibrationEnabledTestId = null;
        }
      }
      // Handle comma-separated IDs (for back-to-back eye tests)
      // Split and add each ID individually to the global array for proper tracking
      newIds.forEach((id) => {
        if (!state.completedTestIds.includes(id)) {
          state.completedTestIds.unshift(id);
        }
      });
    },
    removeCompletedTestIdFromSuite: (state, action: PayloadAction<{ testSuiteTestId: string }>) => {
      const { testSuiteTestId } = action.payload;
      const testIndex = state.tests.findIndex((test) => test.id === testSuiteTestId);
      if (testIndex !== -1) {
        const idsToRemove = new Set(
          parseCompletedTestResultIds(state.tests[testIndex].completedTestResultId),
        );
        state.tests[testIndex].completedTestResultId = undefined;
        if (idsToRemove.size > 0) {
          state.completedTestIds = state.completedTestIds.filter((id) => !idsToRemove.has(id));
        }
      }
    },
    setSuiteMetadata: (
      state,
      action: PayloadAction<{ suiteId: string | null; suiteName: string | null }>,
    ) => {
      state.suiteId = action.payload.suiteId;
      state.suiteName = action.payload.suiteName;
    },
    setAutoPlay: (state, action: PayloadAction<boolean>) => {
      state.autoPlay = action.payload;
    },
    setAutoPlayCountdown: (state, action: PayloadAction<number | null>) => {
      state.autoPlayCountdown = action.payload;
    },
    setSkipNextAutoPlay: (state, action: PayloadAction<boolean>) => {
      state.skipNextAutoPlay = action.payload;
    },
    setTestSuiteSessionId: (state, action: PayloadAction<string | null>) => {
      state.sessionId = action.payload;
    },
    setSuiteRunStartedAt: (state, action: PayloadAction<number | null>) => {
      state.suiteRunStartedAt = action.payload;
      if (action.payload != null) {
        state.suiteRunEndedAt = null;
      }
    },
    setSuiteRunEndedAt: (
      state,
      action: PayloadAction<{ endedAt: number; status: "completed" | "terminated" } | null>,
    ) => {
      state.suiteRunEndedAt = action.payload == null ? null : action.payload.endedAt;
    },

    clearSessionResultReplacement: (state, action: PayloadAction<string>) => {
      delete state.sessionResultReplacement[action.payload];
    },
    startNextTest: (_state) => {},
    loadTestSuite: (
      state,
      action: PayloadAction<{
        suiteId: string;
        suiteName: string;
        tests: TestSuiteItem[];
        activeTestIndex: number | null;
        selectedTestIndex?: number | null;
        completedTestIds?: string[];
        autoPlay?: boolean;
        restoreFirstTestConfiguration?: boolean;
        overview?: TestSuiteOverview | null;
      }>,
    ) => {
      const firstTestCompleted = Boolean(action.payload.tests[0]?.completedTestResultId);
      state.suiteId = action.payload.suiteId;
      state.suiteName = action.payload.suiteName;
      state.overview = action.payload.overview ?? null;
      state.tests = action.payload.tests.map((test, index) =>
        cloneSuiteItemWithDefaultCalibrationSetting(
          {
            ...test,
            configuration: { ...test.configuration },
          },
          index,
        ),
      );
      if (firstTestCompleted) {
        state.tests = disableCalibrationSceneForSuiteTests(state.tests);
      }
      state.activeTestIndex = action.payload.activeTestIndex;
      state.selectedTestIndex = action.payload.selectedTestIndex ?? action.payload.activeTestIndex;
      state.sessionResultReplacement = {};
      state.suiteRunStartedAt = null;
      state.suiteRunEndedAt = null;
      state.skipNextAutoPlay = false;
      state.autoPlayCountdown = null;
      state.firstTestCompleted = firstTestCompleted;
      state.calibrationEnabledTestId = null;
      if (action.payload.completedTestIds !== undefined) {
        state.completedTestIds = [...action.payload.completedTestIds];
      }
      if (action.payload.autoPlay !== undefined) {
        state.autoPlay = action.payload.autoPlay;
      }
    },
    resetTestSuite: () => initialState,
    updateTestSuiteItemConfigurationAfterMutation: (
      _state,
      _action: PayloadAction<{ id: string; configuration: Partial<TestConfiguration> }>,
    ) => {},
    startFirstTest: (_state, _action: PayloadAction<{ testIndex: number }>) => {},
    restartTestInSuite: (
      state,
      action: PayloadAction<{
        testToRemoveId: string;
        testToRestart: {
          id: string;
          configuration: TestConfiguration;
          completedTestResultId?: string;
        };
        testIndex: number;
        skipNextAutoPlay?: boolean;
      }>,
    ) => {
      const { testToRemoveId, testToRestart, testIndex } = action.payload;

      if (testIndex < 0 || testIndex >= state.tests.length) return;

      const oldTest = state.tests[testIndex];
      const isCalibrationEnabledTest = oldTest.id === state.calibrationEnabledTestId;

      if (oldTest.id === testToRemoveId) {
        const idsToRemove = new Set(parseCompletedTestResultIds(oldTest.completedTestResultId));
        if (idsToRemove.size > 0) {
          state.completedTestIds = state.completedTestIds.filter((id) => !idsToRemove.has(id));
        }
      }

      state.tests[testIndex] = {
        id: testToRestart.id,
        configuration: isCalibrationEnabledTest
          ? { ...testToRestart.configuration, startWithCalibrationScene: true }
          : buildRestartConfiguration(testToRestart.configuration, state.firstTestCompleted),
        availableConfigurations: oldTest.availableConfigurations,
        availableProgressConfigurations: oldTest.availableProgressConfigurations,
        progressConfiguration: oldTest.progressConfiguration,
        status: "not started",
        progress: 0,
        completedTestResultId: undefined,
      };
      state.sessionResultReplacement[testToRestart.id] = testToRemoveId;
      state.skipNextAutoPlay = action.payload.skipNextAutoPlay ?? false;
      if (isCalibrationEnabledTest) {
        state.calibrationEnabledTestId = testToRestart.id;
      }
    },
    setTestSuiteSharedConfiguration: (
      state,
      action: PayloadAction<Partial<TestConfiguration> | null>,
    ) => {
      state.sharedTestConfiguration = action.payload;
    },
    setPendingPlayAfterSave: (state, action: PayloadAction<boolean>) => {
      state.pendingPlayAfterSave = action.payload;
    },
    enableCalibrationForTest: (state, action: PayloadAction<string>) => {
      const test = state.tests.find((item) => item.id === action.payload);
      if (!test) return;
      if (state.calibrationEnabledTestId && state.calibrationEnabledTestId !== test.id) {
        const previous = state.tests.find((item) => item.id === state.calibrationEnabledTestId);
        if (previous) previous.configuration.startWithCalibrationScene = false;
      }
      test.configuration.startWithCalibrationScene = true;
      state.calibrationEnabledTestId = test.id;
    },
    setTestSuiteOverview: (state, action: PayloadAction<TestSuiteOverview | null>) => {
      state.overview = action.payload;
    },
  },
});

export const {
  addTest,
  removeTest,
  updateTest,
  updateTestConfiguration,
  updateTestStatus,
  reorderTests,
  setActiveTest,
  setSelectedTest,
  moveToNextTest,
  addCompletedTestIdToSuite,
  removeCompletedTestIdFromSuite,
  setSuiteMetadata,
  setAutoPlay,
  setAutoPlayCountdown,
  setSkipNextAutoPlay,
  setTestSuiteSessionId,
  setSuiteRunStartedAt,
  setSuiteRunEndedAt,
  clearSessionResultReplacement,
  startNextTest,
  loadTestSuite,
  clearTestSuite,
  startFirstTest,
  resetTestSuite,
  updateTestSuiteItemConfigurationAfterMutation,
  restartTestInSuite,
  setTestSuiteSharedConfiguration,
  setPendingPlayAfterSave,
  enableCalibrationForTest,
  setTestSuiteOverview,
} = testSuiteSlice.actions;

export default testSuiteSlice.reducer;
